import PropTypes from 'prop-types';
import React from 'react';
import entityComposition from '../../common/classes/entityComposition';
import { calculate } from '../../../../behaviors/src/behaviors/formulaBehavior';
import { unitsSelector, valueSelector, placeholderSelector, newPathSelector, enabledSelector, labelSelector, conformanceSelector } from '../selectors/index';
import { UncomposedNumberField } from './numberField';
import logValueFunc from '../commonActions/logValue';
import onChangeFunc from '../commonActions/onChange';
import localChangeSelector from '../selectors/localChangeSelector';
const UnitsField = _ref => {
  let unitsTypes = _ref.unitsTypes,
    onChange = _ref.onChange,
    notificationActions = _ref.notificationActions,
    enabled = _ref.enabled,
    path = _ref.path,
    logValue = _ref.logValue,
    operationComplete = _ref.operationComplete,
    operationPending = _ref.operationPending,
    localChangeFunc = _ref.localChangeFunc,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? undefined : _ref$value,
    _ref$maximumFractionD = _ref.maximumFractionDigits,
    maximumFractionDigits = _ref$maximumFractionD === void 0 ? 2 : _ref$maximumFractionD,
    _ref$minimumIntegerDi = _ref.minimumIntegerDigits,
    minimumIntegerDigits = _ref$minimumIntegerDi === void 0 ? 0 : _ref$minimumIntegerDi,
    _ref$minimumFractionD = _ref.minimumFractionDigits,
    minimumFractionDigits = _ref$minimumFractionD === void 0 ? 0 : _ref$minimumFractionD,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
    _ref$displayUnit = _ref.displayUnit,
    displayUnit = _ref$displayUnit === void 0 ? '' : _ref$displayUnit,
    _ref$conformanceText = _ref.conformanceText,
    conformanceText = _ref$conformanceText === void 0 ? null : _ref$conformanceText,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    _ref$testingIdentifie = _ref.testingIdentifier,
    testingIdentifier = _ref$testingIdentifie === void 0 ? 'default-units-field' : _ref$testingIdentifie;
  const storageUnitsIdentifier = Object.keys(unitsTypes).find(key => !unitsTypes[key].toDefault);
  const _ref2 = unitsTypes[displayUnit] || storageUnitsIdentifier,
    currentUnitlabel = _ref2.label,
    fromDefault = _ref2.fromDefault,
    toDefault = _ref2.toDefault;
  const convertValue = (formula, variables) => {
    try {
      const result = calculate(formula, variables);
      return result;
    } catch (message) {
      // id ensures only one popup at a time
      notificationActions.error({
        message,
        id: 'expression_parse_error'
      });
    }
    return null;
  };
  const getDisplayValue = currentValue => {
    if (currentValue === undefined) {
      return undefined;
    }
    if (storageUnitsIdentifier === displayUnit) {
      return value;
    }
    // console.log(fromDefault, currentValue);
    const convertedValue = convertValue(fromDefault, {
      value: currentValue
    });
    const rawValue = convertedValue !== null ? convertedValue : currentValue;
    const fixedValue = (rawValue || 0) === 0 ? rawValue : rawValue.toFixed(maximumFractionDigits);
    // Forcing to a number will turn 12.00 into just plain 12 or 0.0 into 0 for cleaner display
    // but it will do nothing to precision: i.e. 12.32 stays as 12.32
    // console.log(convertedValue, currentValue);
    return fixedValue ? Number(fixedValue) : fixedValue;
  };
  const getSaveValue = currentValue => {
    if (currentValue === null) {
      return null;
    }
    if (storageUnitsIdentifier === displayUnit) {
      return currentValue;
    }
    const convertedValue = convertValue(toDefault, {
      value: currentValue
    });
    return convertedValue !== null ? convertedValue : currentValue;
  };
  const displayValue = getDisplayValue(value);
  const handleChange = async _ref3 => {
    let inPath = _ref3.path,
      changeValue = _ref3.value,
      _ref3$changeModifiers = _ref3.changeModifiers,
      changeModifiers = _ref3$changeModifiers === void 0 ? {} : _ref3$changeModifiers;
    await operationPending();
    const saveValue = getSaveValue(changeValue);
    const log = {
      value: saveValue,
      displayValue: "".concat(changeValue, " ").concat(displayUnit || '')
    };
    await onChange({
      path: inPath,
      value: saveValue,
      changeModifiers
    });

    // log the display value.
    await logValue(log);
    return operationComplete();
  };
  return /*#__PURE__*/React.createElement(UncomposedNumberField, {
    conformanceText: conformanceText,
    enabled: enabled,
    displayUnit: displayUnit,
    label: label,
    maximumFractionDigits: maximumFractionDigits,
    minimumFractionDigits: minimumFractionDigits,
    minimumIntegerDigits: minimumIntegerDigits,
    operationPending: operationPending
    // we send a no-op for operation complete, because
    // we call it in this class level instead when we are all done.
    ,
    operationComplete: () => {},
    testingIdentifier: testingIdentifier,
    onChange: handleChange,
    value: displayValue,
    internalLabel: currentUnitlabel,
    placeholder: placeholder,
    path: path
    // we want to log the value in here instead of
    // in the numberField, so we pass a no-op for
    // this function.
    ,
    logValue: () => {},
    localChangeFunc: localChangeFunc
  });
};
UnitsField.propTypes = {
  conformanceText: PropTypes.string,
  path: PropTypes.string.isRequired,
  displayUnit: PropTypes.string,
  testingIdentifier: PropTypes.string,
  label: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  logValue: PropTypes.func.isRequired,
  operationPending: PropTypes.func.isRequired,
  localChangeFunc: PropTypes.func.isRequired,
  operationComplete: PropTypes.func.isRequired,
  maximumFractionDigits: PropTypes.number,
  minimumIntegerDigits: PropTypes.number,
  minimumFractionDigits: PropTypes.number,
  placeholder: PropTypes.string,
  unitsTypes: PropTypes.shape({}).isRequired,
  format: PropTypes.shape({}).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  notificationActions: PropTypes.shape({
    error: PropTypes.func
  }).isRequired
};
export { UnitsField as UncomposedUnitsField };
export default entityComposition(UnitsField, [], {
  conformanceText: conformanceSelector,
  displayUnit: unitsSelector,
  value: valueSelector,
  placeholder: placeholderSelector,
  path: newPathSelector,
  enabled: enabledSelector,
  label: labelSelector,
  logValue: logValueFunc,
  onChange: onChangeFunc,
  localChangeFunc: localChangeSelector
}, ['notification']);